import { useCallback, useEffect, useMemo } from 'react'
import { Box, makeStyles, Theme, Typography } from '@material-ui/core'
import MDTable from './MDTable'
import { FormattedMessage } from 'react-intl'
import { WarningCircleIcon } from '@src/icons'
import { useMaterialsDirectLazyQuery } from '@src/queries/MaterialsDirectQuery.generated'
import ItemsSkeleton from '../ItemsSkeleton'
import ListError from '@src/components/ListError'
import OrderCenterLayout from '../OrderCenterLayout'
import MDSearchForm from './MDSearchForm'
import { useCurrentUser } from '@src/utils/useCurrentUser'
import { usePartnerAddressesQuery } from '@src/queries/PartnerAddressesQuery.generated'
import { MaterialsDirectInput } from '@src/types/graphql-types'
import { useRouter } from 'next/router'
import ResponsivePagination from '@src/components/ResponsivePagination'
import MDDownloadButton from './MDDownloadButton'

const useStyles = makeStyles((theme: Theme) => ({
  content: {
    paddingRight: theme.spacing(5),
    paddingLeft: theme.spacing(5),

    [theme.breakpoints.up('md')]: {
      padding: 0,
    },
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  warning: {
    display: 'flex',
    gap: theme.spacing(2),
    fontWeight: 700,
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(8),

    '& svg': {
      fill: theme.palette.vibrant.yellow,
    },
  },
  error: {
    padding: theme.spacing(6, 4),

    [theme.breakpoints.up('md')]: {
      padding: 0,
    },
  },
  pagination: {
    margin: theme.spacing(4, 0),
  },
  downloadContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
  },
}))

export enum MaterialsDirectStatusEnum {
  BILLING_ITEM_BLOCKED = 'BILLING_ITEM_BLOCKED',
  BLOCKED = 'BLOCKED',
  DELIVERY_ITEM_BLOCKED = 'DELIVERY_ITEM_BLOCKED',
  INVOICED = 'INVOICED',
  OPEN = 'OPEN',
  REJECTED = 'REJECTED',
  SHIPPED = 'SHIPPED',
}

const MaterialsDirect = () => {
  const classes = useStyles()
  const { soldToNumber } = useCurrentUser()
  const router = useRouter()
  const DEFAULT_PAGE = 1
  const DEFAULT_PAGE_SIZE = 20
  const DEFAULT_SHIP_TO = 'ALL'

  const {
    country,
    language,
    soldTo,
    shipToList: selectedShipTo,
    page,
    pageSize,
    status,
    orderNo,
    poNo,
    customerMaterialNo,
    lotNo,
    contactParty,
  } = router.query || {}

  const { data: partnerData, loading: loadingPartnerAddress } =
    usePartnerAddressesQuery({
      variables: {
        input: {
          soldTos: [soldToNumber || ''],
        },
      },
      ssr: false,
      skip: !soldToNumber,
    })

  const defaultShipToList = useMemo(
    () =>
      partnerData?.getPartnerAddresses?.shipTos?.map((p) => p.accountNumber) ||
      [],
    [partnerData?.getPartnerAddresses?.shipTos]
  )

  const initialValues = useMemo(
    () => ({
      soldTo: soldTo || soldToNumber!,
      shipToList:
        selectedShipTo && selectedShipTo !== DEFAULT_SHIP_TO
          ? [selectedShipTo]
          : DEFAULT_SHIP_TO,
      status: status || DEFAULT_SHIP_TO,
      page: Number(page) || DEFAULT_PAGE,
      pageSize: Number(pageSize) || DEFAULT_PAGE_SIZE,
      orderNo: orderNo || undefined,
      poNo: poNo || undefined,
      customerMaterialNo: customerMaterialNo || '',
      lotNo: lotNo || '',
      contactParty: contactParty || '',
    }),
    [
      soldTo,
      soldToNumber,
      selectedShipTo,
      status,
      page,
      pageSize,
      orderNo,
      poNo,
      customerMaterialNo,
      lotNo,
      contactParty,
    ]
  )

  const [fetchMaterialsDirectOrders, { loading, error, data }] =
    useMaterialsDirectLazyQuery({
      ssr: false,
    })

  const { page: pagination } = data?.fetchMaterialsDirectOrders || {}

  const isShipToSelected: boolean = !!(
    selectedShipTo && selectedShipTo !== DEFAULT_SHIP_TO
  )

  const buildSearchInput = useCallback(
    (searchQuery: MaterialsDirectInput) => {
      const soldToValue = searchQuery.soldTo || soldToNumber || ''
      const shipToValue =
        isShipToSelected && selectedShipTo
          ? [selectedShipTo]
          : [...defaultShipToList]

      return {
        soldTo: soldToValue,
        shipToList: shipToValue,
        page: Number(searchQuery.page) || DEFAULT_PAGE,
        pageSize: Number(searchQuery.pageSize) || DEFAULT_PAGE_SIZE,
        status: MaterialsDirectStatusEnum[searchQuery.status!] || '',
        orderNo: searchQuery.orderNo || undefined,
        poNo: searchQuery.poNo || undefined,
        customerMaterialNo: searchQuery.customerMaterialNo || '',
        lotNo: searchQuery.lotNo || '',
        contactParty: searchQuery.contactParty || '',
      }
    },
    [soldToNumber, defaultShipToList, isShipToSelected, selectedShipTo]
  )

  const handleSubmit = (searchQuery) => {
    const searchInput = buildSearchInput(searchQuery)

    fetchMaterialsDirectOrders({
      variables: { input: searchInput },
    })

    router.push(
      {
        pathname: router.pathname,
        query: {
          ...searchQuery,
          country: router.query.country,
          language: router.query.language,
        },
      },
      undefined,
      { shallow: true }
    )
  }

  const clearSearch = () => {
    fetchMaterialsDirectOrders({
      variables: {
        input: {
          soldTo: soldToNumber || '',
          shipToList: defaultShipToList,
          page: DEFAULT_PAGE,
          pageSize: DEFAULT_PAGE_SIZE,
          status: '',
          orderNo: undefined,
          poNo: undefined,
          customerMaterialNo: '',
          lotNo: '',
          contactParty: '',
        },
      },
    })

    router.replace(
      {
        pathname: router.pathname,
        query: {
          country,
          language,
        },
      },
      undefined,
      { shallow: true }
    )
  }

  const handleUpdatePage = (nextPage: number) => {
    handleSubmit({
      soldTo: soldToNumber!,
      shipToList: selectedShipTo || DEFAULT_SHIP_TO,
      page: nextPage,
      pageSize: Number(pageSize) || DEFAULT_PAGE_SIZE,
      status,
      orderNo,
      poNo,
      customerMaterialNo,
      lotNo,
    })
  }

  useEffect(() => {
    if (soldToNumber && defaultShipToList?.length > 0) {
      const searchInput = buildSearchInput({
        soldTo: soldTo || soldToNumber,
        page: Number(page) || DEFAULT_PAGE,
        pageSize: Number(pageSize) || DEFAULT_PAGE_SIZE,
        status,
        orderNo,
        poNo,
        customerMaterialNo,
        lotNo,
        contactParty,
      })

      fetchMaterialsDirectOrders({
        variables: { input: searchInput },
      })
    }
  }, [defaultShipToList.length])

  return (
    <OrderCenterLayout isNavEnabled={false}>
      <Box className={classes.content}>
        <Box className={classes.title}>
          <Typography variant="h1">
            <FormattedMessage id="ORDERS" defaultMessage="Orders" />
          </Typography>
        </Box>

        <Typography variant="body1">
          <FormattedMessage
            id="REAL_TIME_UPDATES_TO_KEEP_YOU_INFORMED_ABOUT_THE_STATUS_OF_YOUR_ORDERS"
            defaultMessage="Real-time updates to keep you informed about the status of your orders."
          />
        </Typography>

        <Box className={classes.warning}>
          <WarningCircleIcon />
          <FormattedMessage
            id="ONLY_THE_LAST_6_MONTHS_OF_DATA_ARE_DISPLAYED_BELOW"
            defaultMessage="Only the last 6 months of data are displayed below."
          />
        </Box>

        <MDSearchForm
          initialValues={initialValues}
          handleSubmit={handleSubmit}
          clearSearch={clearSearch}
        />

        {(loading || loadingPartnerAddress) && defaultShipToList.length > 0 && (
          <ItemsSkeleton />
        )}

        {error && (
          <Box className={classes.error}>
            <ListError />
          </Box>
        )}

        {data && (
          <Box className={classes.downloadContainer}>
            <MDDownloadButton
              params={{
                soldTo: soldToNumber!,
                shipToList: selectedShipTo || defaultShipToList,
              }}
            />
          </Box>
        )}

        {data && <MDTable data={data} />}

        {pagination && pagination.totalPages! > 1 && (
          <Box className={classes.pagination}>
            <ResponsivePagination
              numPages={pagination.totalPages!}
              currentPage={pagination.number!}
              handleUpdatePage={handleUpdatePage}
            />
          </Box>
        )}
      </Box>
    </OrderCenterLayout>
  )
}

export default MaterialsDirect
